'use client';

import { createContext, useContext, ReactNode, useEffect, useState, useRef } from 'react';
import { create, StoreApi, UseBoundStore, useStore } from 'zustand';
import users from '@/lib/firebase/collections/users';
import { useClerkAuthStore } from '@/stores/ClerkAuthStore';
import { UserFirebaseTypeWithId } from '@poppy/firebase';
import repos from '@/lib/firebase/repos';
import { auth, onAuthStateChanged } from '@/lib/firebase/firebase';

// -------------------------------------
// Types
// -------------------------------------

export interface UserState {
  user: UserFirebaseTypeWithId | null;
  isLoading: boolean;
}
export type UserStoreState = UserState;

// -------------------------------------
// Store Factory
// -------------------------------------

export const createUserStore = () => create<UserStoreState>(() => ({
  user: null,
  isLoading: true
}));
export let userStore: UseBoundStore<StoreApi<UserState>> | null = null;

// -------------------------------------
// Context Setup
// -------------------------------------

const fallbackUserStore = createUserStore();
const UserStoreContext = createContext<ReturnType<typeof createUserStore>>(fallbackUserStore);
export const UserStoreProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const clerkUserId = useClerkAuthStore(state => state.user?.id);
  const clerkIsLoaded = useClerkAuthStore(state_0 => state_0.isLoaded);
  const [isFirebaseReady, setIsFirebaseReady] = useState(false);
  userStore = useRef(createUserStore()).current;

  // Listen to Firebase auth state changes
  // Wait for ID token to be ready before allowing Firestore queries
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      if (firebaseUser) {
        try {
          // Wait for the ID token to be ready - this ensures Firestore can authenticate
          await firebaseUser.getIdToken();
          setIsFirebaseReady(true);
        } catch (error) {
          console.error('Failed to get Firebase ID token:', error);
          setIsFirebaseReady(false);
        }
      } else {
        setIsFirebaseReady(false);
      }
    });
    return unsubscribe;
  }, []);
  useEffect(() => {
    if (!clerkIsLoaded) {
      userStore?.setState({
        isLoading: true
      });
      return;
    }
    if (!clerkUserId) {
      // User logged out - clear Firebase user
      userStore?.setState({
        user: null
      });
      userStore?.setState({
        isLoading: false
      });
      return;
    }
  }, [clerkUserId, clerkIsLoaded]);
  useEffect(() => {
    // Guard: wait for both Clerk and Firebase auth to be ready
    if (!clerkUserId || !isFirebaseReady) return;

    // User logged in - fetch and subscribe to Firebase user
    const fetchAndSubscribe = async () => {
      userStore?.setState({
        isLoading: true
      });
      try {
        const {
          items
        } = await repos.collection('users').find({
          where: [['authUserId', '==', clerkUserId]]
        });
        if (items.length === 0) return;
        const firebaseUser_0 = items[0];
        if (firebaseUser_0) {
          userStore?.setState({
            user: firebaseUser_0
          });

          // 🆕 Set up real-time subscription for user data changes
          const unsubscribe_0 = users.subscribe(firebaseUser_0.id, updatedUser => {
            userStore?.setState({
              user: updatedUser
            });
            console.log('🔄 Global user data updated:', updatedUser.email);
          });
          return unsubscribe_0;
        } else {
          userStore?.setState({
            user: null
          });
        }
      } catch (error_0) {
        console.error('Failed to fetch Firebase user:', error_0);
        userStore?.setState({
          user: null
        });
      } finally {
        userStore?.setState({
          isLoading: false
        });
      }
    };
    const unsubscribePromise = fetchAndSubscribe();

    // Cleanup subscription when clerkUser changes or component unmounts
    return () => {
      unsubscribePromise?.then(unsub => unsub?.());
    };
  }, [clerkUserId, isFirebaseReady]);
  return <UserStoreContext.Provider value={userStore}>{children}</UserStoreContext.Provider>;
};

// -------------------------------------
// Hook
// -------------------------------------

export function useUserStore(selector) {
  const store = useContext(UserStoreContext);
  return useStore(store, selector);
}